/**
 * CoachModeToggle (iter150) — mirror of UserModeToggle for coach/admin
 * accounts. Lives next to the bell so admins can flip Simplified Mode from
 * anywhere instead of having to hunt for it on the Coach Profile page.
 * Writes through `PUT /me/coach/simplified-mode` and updates the auth user
 * doc so `CoachDashboard` re-renders immediately.
 */
import { useState, useEffect } from "react";
import { Settings2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const NUDGE_KEY = "rallylab.coach_mode_toggle_seen";

export default function CoachModeToggle({ compact = false }) {
  const { user, setUser } = useAuth();
  const [busy, setBusy] = useState(false);
  // Iter151 — First-time coaches see a subtle "Try Simple Mode" pulse on
  // the toggle so they discover it without reading docs. Dismissed on
  // first click + persisted in localStorage.
  const [showNudge, setShowNudge] = useState(() => {
    try { return localStorage.getItem(NUDGE_KEY) !== "1"; } catch { return false; }
  });
  const dismissNudge = () => {
    try { localStorage.setItem(NUDGE_KEY, "1"); } catch { /* noop */ }
    setShowNudge(false);
  };

  // Only coach/admin accounts see this. Students already have UserModeToggle.
  const perms = user?.permissions || [user?.role].filter(Boolean);
  const isCoach = user?.role === "coach" || user?.role === "admin"
    || perms.includes("coach") || perms.includes("admin") || perms.includes("super_admin");
  if (!user || !isCoach) return null;

  // Default ON per iter143 migration; treat missing as ON so the toggle isn't
  // misleadingly showing "Simple" for someone already using the launcher.
  const isSimple = user.simplified_mode === true;

  const flip = async () => {
    if (busy) return;
    dismissNudge();
    setBusy(true);
    const next = !isSimple;
    try {
      const { data } = await api.put("/me/coach/simplified-mode", {
        enabled: next,
        tiles: user.simplified_tiles || null,
      });
      if (setUser) {
        setUser((u) => u ? {
          ...u,
          simplified_mode: !!data.simplified_mode,
          simplified_tiles: data.simplified_tiles || u.simplified_tiles,
        } : u);
      }
      toast.success(next
        ? "Simplified Mode ON — big tiles, focused on bookings + upcoming sessions."
        : "Full Dashboard restored.");
    } catch {
      toast.error("Could not switch mode — try again.");
    } finally { setBusy(false); }
  };

  return (
    <div className="relative inline-flex" data-testid="coach-mode-toggle-wrap">
      <button
        type="button"
        onClick={flip}
        disabled={busy}
        data-testid="coach-mode-toggle"
        aria-label={isSimple ? "Switch to Full Dashboard" : "Switch to Simplified Mode"}
        title={isSimple ? "Switch to Full Dashboard" : "Switch to Simplified Mode"}
        className={
          "inline-flex items-center gap-1.5 border transition-colors " +
          (compact ? "h-9 px-3 text-[10px]" : "h-10 px-4 text-xs") +
          " hover:brightness-110 disabled:opacity-50"
        }
        style={{
          background: "var(--surface)",
          borderColor: "var(--border)",
          color: isSimple ? "var(--volt, #CCFF00)" : "var(--text)",
          borderRadius: 999,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          fontWeight: 700,
        }}
      >
        <Settings2 className={compact ? "w-3 h-3" : "w-3.5 h-3.5"} strokeWidth={1.75} />
        <span>{isSimple ? "Switch to Full Dashboard" : "Switch to Simple Mode"}</span>
      </button>
      {showNudge && !isSimple && (
        <span
          data-testid="coach-mode-toggle-nudge"
          className="absolute -bottom-1 -right-1 w-2.5 h-2.5 rounded-full bg-[#CCFF00] animate-ping"
          aria-hidden="true"
        />
      )}
      {showNudge && !isSimple && (
        <span
          className="absolute -bottom-1 -right-1 w-2.5 h-2.5 rounded-full bg-[#CCFF00]"
          aria-hidden="true"
        />
      )}
    </div>
  );
}
